<script setup lang="ts">
import type { RelatedCompany } from '~/api/related/companies'

const { t } = useAppI18n()

const props = defineProps<{
  data: RelatedCompany
  isSingle?: boolean
}>()

const emit = defineEmits<{
  click: [company: RelatedCompany]
}>()

const companyNameContainerRef = ref<HTMLElement | null>(null)
const companyNameRef = ref<HTMLElement | null>(null)
const isCompanyNameOverflow = ref(false)
let companyNameResizeObserver: ResizeObserver | null = null

/** 返回企业卡片的稳定 key，兼容接口暂未返回企业 ID 的情况。 */
const companyKey = computed(() => props.data.companyId || props.data.companyName || 'company')

onMounted(() => {
  checkCompanyNameOverflow()

  if (import.meta.client && companyNameContainerRef.value) {
    companyNameResizeObserver = new ResizeObserver(checkCompanyNameOverflow)
    companyNameResizeObserver.observe(companyNameContainerRef.value)
  }
})

onBeforeUnmount(() => {
  companyNameResizeObserver?.disconnect()
})

watch(
  () => props.data.companyName,
  () => {
    nextTick(checkCompanyNameOverflow)
  },
)

/** 根据企业名称节点的实际尺寸判断是否发生文本溢出。 */
const checkCompanyNameOverflow = () => {
  const element = companyNameRef.value

  if (!element) {
    isCompanyNameOverflow.value = false
    return
  }

  isCompanyNameOverflow.value = element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
}

/** 点击企业卡片，打开 WikiBit APP 下载弹窗。 */
const handleCompanyClick = () => {
  emit('click', props.data)
}
</script>

<template>
  <article
    :data-company-key="companyKey"
    class="flex min-w-0 cursor-pointer flex-col gap-[12px] rounded-[8px]"
    :class="
      isSingle
        ? 'hover:bg-[#F8F8F8] hover:shadow-[0_0_0_4px_#F8F8F8]'
        : 'hover:bg-[#F8F8F8] hover:shadow-[0_0_0_4px_#F8F8F8]'
    "
    role="button"
    tabindex="0"
    @click="handleCompanyClick"
    @keydown.enter="handleCompanyClick"
    @keydown.space.prevent="handleCompanyClick"
  >
    <div class="flex min-w-0 items-start gap-[12px]" :class="{ '': !isSingle }">
      <div class="relative size-[40px] shrink-0">
        <CommonImage
          :src="data.ico || data.logo || ''"
          :alt="data.companyName || 'company'"
          width="40"
          height="40"
          loading="lazy"
          class="size-[40px] rounded-[4px] object-contain"
        />
        <CommonImage
          v-if="data.countryCircleFlag || data.countryFlag"
          :src="data.countryCircleFlag || data.countryFlag || ''"
          alt=""
          loading="lazy"
          class="absolute end-[-2.5px] bottom-[-2.5px] !size-[18px] rounded-[50%] border border-white object-cover"
          aria-hidden="true"
        />
      </div>
      <div ref="companyNameContainerRef" class="min-w-0 flex-1">
        <el-popover
          v-if="isCompanyNameOverflow"
          placement="top"
          trigger="hover"
          :z-index="3020"
          width="auto"
          popper-style="padding: 12px; border-radius: 8px;"
        >
          {{ data.companyName || '--' }}
          <template #reference>
            <h3
              ref="companyNameRef"
              class="words-break-lines m-0 line-clamp-1 min-h-[24px] text-[16px] leading-[24px] font-[500] text-[#7D818A]"
              :style="{ color: data.registrationStatus !== 2 ? '#292A2E' : '#7D818A' }"
            >
              {{ data.companyName || '--' }}
            </h3>
          </template>
        </el-popover>
        <h3
          v-else
          ref="companyNameRef"
          class="words-break-lines m-0 line-clamp-1 min-h-[24px] text-[16px] leading-[24px] font-[500] text-[#7D818A]"
          :style="{ color: data.registrationStatus !== 2 ? '#292A2E' : '#7D818A' }"
        >
          {{ data.companyName || '--' }}
        </h3>
        <div class="mt-[2px] flex min-w-0 flex-wrap items-center gap-[8px] text-[12px] leading-[18px]">
          <span class="shrink-0" :style="{ color: data?.registrationStatusFontColor }">{{
            data?.registrationStatusName || '--'
          }}</span>
          <span
            v-if="data?.countryName && data.registrationStatusName"
            class="h-[8px] w-[1px] shrink-0 bg-[rgba(11,18,40,0.14)]"
            aria-hidden="true"
          ></span>
          <span v-if="data?.countryName" class="country-country truncate text-[#505258]">{{ data.countryName }}</span>
        </div>
      </div>
    </div>

    <div
      class="grid gap-[8px] rounded-[8px] border border-[rgba(5,21,36,0.06)] bg-white p-[16px]"
      :class="isSingle ? 'grid-cols-2' : 'h-[78px] grid-cols-2'"
    >
      <div class="min-w-0" :class="{ 'flex items-center gap-[8px]': isSingle }">
        <p class="m-0 truncate text-[14px] leading-[20px] text-[#7D818A]">{{ t('89168') }}</p>
        <p class="m-0 truncate text-[14px] leading-[22px] font-[500] text-[#292A2E]" :class="{ 'mt-[4px]': !isSingle }">
          {{ data.registrationNumber || '--' }}
        </p>
      </div>
      <div class="flex min-w-0" :class="isSingle ? 'items-center gap-[8px]' : 'flex-col justify-between'">
        <p class="m-0 truncate text-[14px] leading-[20px] text-[#7D818A]">{{ t('84672') }}</p>
        <p class="m-0 truncate text-[14px] leading-[22px] font-[500] text-[#292A2E]" :class="{ 'mt-[4px]': !isSingle }">
          {{ data.foundDate || '--' }}
        </p>
      </div>
    </div>
  </article>
</template>
